import React, { useEffect, useRef, useCallback } from "react";
import {
  Card,
  Typography,
  useTheme,
  CircularProgress,
  Box,
} from "@mui/material";
import * as d3 from "d3";
import { tokens } from "../theme";
import { FunnelStage } from "Models/insights-result";

interface FunnelAnalyticsProps {
  stages: FunnelStage[];
  loading: boolean;
}

const FunnelAnalytics: React.FC<FunnelAnalyticsProps> = ({
  stages,
  loading,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const svgRef = useRef<SVGSVGElement>(null);

  const drawFunnel = useCallback(
    (data: FunnelStage[]) => {
      if (!svgRef.current) return;

      // Clear any existing SVG content
      d3.select(svgRef.current).selectAll("*").remove();

      // ✅ Exit if no data
      if (!data || data.length === 0) return;

      const containerWidth = svgRef.current.parentElement?.clientWidth || 1200;
      const margin = { top: 70, right: 40, bottom: 20, left: 40 };
      const width = containerWidth - margin.left - margin.right;
      const height = 60;

      const svg = d3
        .select(svgRef.current)
        .attr("width", "100%")
        .attr("height", height + margin.top + margin.bottom)
        .attr(
          "viewBox",
          `0 0 ${containerWidth} ${height + margin.top + margin.bottom}`
        )
        .attr("preserveAspectRatio", "xMidYMid meet");

      const generateStageColors = (count: number) => {
        const baseHue = 230; // Indigo / blue range
        const saturation = 85;
        const lightnessStart = 92;
        const lightnessEnd = 45;

        return Array.from({ length: count }, (_, i) => {
          const lightness =
            lightnessStart -
            (i * (lightnessStart - lightnessEnd)) / Math.max(count - 1, 1);

          return `hsl(${baseHue}, ${saturation}%, ${lightness}%)`;
        });
      };

      const stageColors = generateStageColors(data.length);

      // Calculate percentages relative to the first stage
      const baseValue = data[0]?.count || 1;
      const percentages = data.map((d) => (d.count / baseValue) * 100);

      // Calculate funnel dimensions
      const funnelHeight = height * 0.6; // 60% of available height
      const funnelTop = margin.top + (height - funnelHeight) / 2;
      const stageWidth = width / data.length;
      const maxStageHeight = funnelHeight;

      // Create funnel group
      const funnelGroup = svg
        .append("g")
        .attr("transform", `translate(${margin.left},0)`);

      // Draw funnel stages
      data.forEach((stage, i: number) => {
        const leftHeight =
          i === 0
            ? maxStageHeight
            : (percentages[i - 1] / 100) * maxStageHeight;
        const rightHeight = (percentages[i] / 100) * maxStageHeight;
        const x = i * stageWidth;

        // Calculate points for trapezoid
        const points = [
          [x, funnelTop + (maxStageHeight - leftHeight) / 2], // top left
          [x + stageWidth, funnelTop + (maxStageHeight - rightHeight) / 2], // top right
          [x + stageWidth, funnelTop + (maxStageHeight + rightHeight) / 2], // bottom right
          [x, funnelTop + (maxStageHeight + leftHeight) / 2], // bottom left
        ];

        // Draw stage
        funnelGroup
          .append("path")
          .attr("d", d3.line()(points as [number, number][]))
          .attr("fill", stageColors[i])
          .attr("stroke", "white")
          .attr("stroke-width", 1);

        // Add value text
        funnelGroup
          .append("text")
          .attr("x", x + stageWidth / 2)
          .attr("y", margin.top - 15)
          .attr("text-anchor", "middle")
          .attr("fill", "#111827")
          .attr("font-size", "22px")
          .attr("font-weight", "700")
          .text(stage.count);

        // Add stage name
        funnelGroup
          .append("text")
          .attr("x", x + stageWidth / 2)
          .attr("y", margin.top - 45)
          .attr("text-anchor", "middle")
          .attr("fill", "#6B7280")
          .attr("font-size", "10px")
          .text(stage.label);
      });

      // Add dividing lines
      data.forEach((_, i: number) => {
        const x: number = margin.left + i * stageWidth;
        const leftHeight =
          i === 0
            ? maxStageHeight
            : (percentages[i - 1] / 100) * maxStageHeight;
        const rightHeight = (percentages[i] / 100) * maxStageHeight;
        const bottomY = funnelTop + (maxStageHeight + rightHeight) / 2; // bottom of the current stage

        // Draw vertical lines
        svg
          .append("line")
          .attr("x1", x)
          .attr("y1", bottomY)
          .attr("x2", x)
          .attr("y2", margin.top - 55)
          .attr("stroke", "#E5E7EB")
          .attr("stroke-width", 1);

        if (i === data.length - 1) {
          const lastBottomY = funnelTop + (maxStageHeight + rightHeight) / 2;

          svg
            .append("line")
            .attr("x1", x + stageWidth)
            .attr("y1", lastBottomY)
            .attr("x2", x + stageWidth)
            .attr("y2", margin.top - 55)
            .attr("stroke", "#E5E7EB")
            .attr("stroke-width", 1);
        }
      });
    },
    [svgRef]
  );

  useEffect(() => {
    drawFunnel(stages);
  }, [stages, drawFunnel]);

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        p: 3,
        my: 2,
        ml: 2,
        mr: 3,
        borderColor: colors.gray[800],
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          px: 3,
        }}
      >
        <Typography
          variant="subtitle1"
          color={colors.gray[200]}
          bgcolor={colors.gray[900]}
          fontWeight="bold"
          sx={{ p: 1, px: 3, display: "inline-block" }}
        >
          Funnel Analytics
        </Typography>
        {loading && (
          <CircularProgress size={24} sx={{ color: colors.primary[500] }} />
        )}
      </Box>

      <svg ref={svgRef} />
    </Card>
  );
};

export default FunnelAnalytics;
